import { useState } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Select from 'react-select'
import { KeyValuePairs, InputContainer, PhotoCropper } from 'components/ideal-core-ui'
import s from './ContactModal.scss'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { makePostRequest } from 'utils/api'
import { SALUTATIONS } from '../constants'
import { AutosuggestInput } from 'components/common/AutosuggestInput'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import { utcDate, timezoneDate } from 'utils/helpers'
import { getDateFormat } from 'utils/authService'
import moment from 'moment-timezone'

const ContactModal = ({
  contact,
  priorities,
  statuses,
  showContactModal,
  toggleContactModal,
  changeProperty,
  saveContact,
  showContactError,
  setFile,
  setCrop,
  currentCompany,
  users,
  isEdit
}) => {
  const {
    id,
    firstName,
    lastName,
    title,
    company,
    priority,
    status,
    birthday,
    partner,
    partnerBirthday,
    label,
    phone,
    phoneExt,
    fax,
    cellPhone,
    email,
    facebook,
    linkedin,
    twitter,
    instagram,
    about,
    photo,
    salutation,
    accountOwner
  } = contact

  const [isAddCompanyVisible, setIsAddCompanyVisible] = useState(false)
  const [name, setName] = useState('')
  const [legalName, setLegalName] = useState('')
  const [hasDuplicateError, setHasDuplicateError] = useState(false)

  const resetCompany = () => {
    setName('')
    setLegalName('')
    setIsAddCompanyVisible(false)
  }

  const saveCompany = async () => {
    try {
      const formData = new FormData()
      formData.append('companyData', JSON.stringify({ name, legalName }))

      const response = await makePostRequest('/v1/companies', formData)
      changeProperty(
        {
          value: response.id,
          label: response.name
        },
        'company'
      )

      resetCompany()
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  const validateProperty = (value, suggestions) => {
    const error = !!suggestions.filter(
      suggestion => suggestion.title.trim().toLowerCase() === value.trim().toLowerCase()
    ).length
    setHasDuplicateError(error)
  }

  return (
    <Modal
      keyboard={false}
      backdrop="static"
      isOpen={showContactModal}
      toggle={toggleContactModal}
      className={s.contactModal}
      size="lg"
    >
      <ModalHeader toggle={toggleContactModal} className={s.modalHeader}>
        {isEdit ? 'Edit' : 'Add New'} Contact
      </ModalHeader>
      <ModalBody>
        {showContactError && <p className={s.error}>All required fields must have a value</p>}
        <div className={s.imageContainer}>
          <PhotoCropper onFileChange={setFile} onCropComplete={setCrop} photo={photo} />
        </div>
        <div className={s.wrapper}>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: 'Salutation',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'salutation')}
                      options={SALUTATIONS}
                      value={salutation}
                    />
                  )
                },
                {
                  key: '*First Name',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter First Name"
                      cb={value => changeProperty(value, 'firstName')}
                      text={firstName}
                      cy="contact_firstname_input"
                    />
                  )
                },
                {
                  key: '*Last Name',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Last Name"
                      cb={value => changeProperty(value, 'lastName')}
                      text={lastName}
                      cy="contact_lastname_input"
                    />
                  )
                },
                {
                  key: '*Email',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Email"
                      cb={value => changeProperty(value, 'email')}
                      text={email}
                      cy="contact_email_input"
                    />
                  )
                },
                !id && !currentCompany?.value
                  ? {
                      key: (
                        <>
                          <span>* Company</span>
                          {/* TODO Add check to display this button */}
                          <span
                            className={s.addCompany}
                            data-cy="add_new_company_btn"
                            onClick={() => setIsAddCompanyVisible(true)}
                          >
                            Add New Company
                          </span>
                        </>
                      ),
                      value: (
                        <PaginatedSelect
                          url="/v1/companies"
                          value={company}
                          onChange={value => changeProperty(value, 'company')}
                          serializer={toCompaniesSelect}
                          id="company_type_select_cy"
                        />
                      )
                    }
                  : {},
                isAddCompanyVisible
                  ? {
                      key: <span className={s.addCompanyTitle}>Add Company</span>,
                      sectionClass: [s.addCompanySection],
                      value: (
                        <>
                          {hasDuplicateError && (
                            <p className={s.error}>
                              Another company with the same name already exists
                            </p>
                          )}
                          <AutosuggestInput
                            type="COMPANY"
                            placeholder="*Company Name"
                            onChangeCb={(value, suggestions) => {
                              setName(value)
                              validateProperty(value, suggestions)
                            }}
                            length="3"
                            className={s.companyAutosuggest}
                            initialValue={name}
                            cy="company_name_input"
                          />
                          <InputContainer
                            type="text"
                            classname={s.addCompanyInput}
                            placeholder="Company Legal Name"
                            cb={setLegalName}
                            text={legalName}
                            cy="company_legal_name_input"
                          />
                          <Button
                            disabled={!name || !legalName}
                            className={s.saveBtn}
                            onClick={saveCompany}
                            data-cy="save_company_btn"
                          >
                            Save Company
                          </Button>
                        </>
                      )
                    }
                  : null,
                {
                  key: 'Title',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Title"
                      cb={value => changeProperty(value, 'title')}
                      text={title}
                    />
                  )
                },
                {
                  key: 'Cell Phone',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Cell Phone"
                      cb={value => changeProperty(value, 'cellPhone')}
                      text={cellPhone}
                    />
                  )
                },
                {
                  key: 'Office Phone',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Office Phone"
                      cb={value => changeProperty(value, 'phone')}
                      text={phone}
                    />
                  )
                },
                {
                  key: 'Ext.',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Ext."
                      cb={value => changeProperty(value, 'phoneExt')}
                      text={phoneExt}
                    />
                  )
                },
                {
                  key: 'Fax',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Fax"
                      cb={value => changeProperty(value, 'fax')}
                      text={fax}
                    />
                  )
                },
                {
                  key: 'Labels',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Separated by comma"
                      cb={value => changeProperty(value, 'label')}
                      text={label}
                    />
                  )
                }
              ]}
            />
          </div>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: 'Status',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'status')}
                      options={statuses}
                      value={status}
                    />
                  )
                },
                {
                  key: 'Priority',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'priority')}
                      options={priorities}
                      value={priority}
                    />
                  )
                },
                {
                  key: 'Contact Owner',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'accountOwner')}
                      options={users}
                      value={accountOwner}
                    />
                  )
                },
                {
                  key: 'Contact Birthday',
                  value: (
                    <DatePicker
                      selected={
                        birthday
                          ? moment(timezoneDate(birthday), `${getDateFormat()}`).toDate()
                          : ''
                      }
                      onChange={value => {
                        if (!value) return
                        changeProperty(utcDate(value), 'birthday')
                      }}
                      dateFormat={`${getDateFormat().replace('YYYY', 'yyyy').replace('DD', 'dd')}`}
                      placeholderText="Select birthday"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      className={s.birthday}
                    />
                  )
                },
                {
                  key: 'Spouse/Partner Name',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Spouse/Partner Name"
                      cb={value => changeProperty(value, 'partner')}
                      text={partner}
                    />
                  )
                },
                {
                  key: 'Spouse/Partner Birthday',
                  value: (
                    <DatePicker
                      selected={
                        partnerBirthday
                          ? moment(timezoneDate(partnerBirthday), `${getDateFormat()}`).toDate()
                          : ''
                      }
                      onChange={value => {
                        if (!value) return
                        changeProperty(utcDate(value), 'partnerBirthday')
                      }}
                      dateFormat={`${getDateFormat().replace('YYYY', 'yyyy').replace('DD', 'dd')}`}
                      placeholderText="Select spouse/partner birthday"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      className={s.birthday}
                    />
                  )
                },
                {
                  key: 'Facebook',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Facebook"
                      cb={value => changeProperty(value, 'facebook')}
                      text={facebook}
                    />
                  )
                },
                {
                  key: 'Linkedin',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Linkedin"
                      cb={value => changeProperty(value, 'linkedin')}
                      text={linkedin}
                    />
                  )
                },
                {
                  key: 'Twitter',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Twitter"
                      cb={value => changeProperty(value, 'twitter')}
                      text={twitter}
                    />
                  )
                },
                {
                  key: 'Instagram',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Instagram"
                      cb={value => changeProperty(value, 'instagram')}
                      text={instagram}
                    />
                  )
                }
              ]}
            />
          </div>
        </div>
        <div className={s.aboutWrapper}>
          <KeyValuePairs
            pairs={[
              {
                key: 'About Info',
                keyClass: [s.label],
                valueClass: [s.value],
                value: (
                  <InputContainer
                    type="textarea"
                    classname={s.aboutInput}
                    placeholder="Enter info about this contact"
                    cb={value => changeProperty(value, 'about')}
                    text={about}
                  />
                )
              }
            ]}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          data-cy="save_btn"
          className={s.saveBtn}
          onClick={() => {
            saveContact()
            resetCompany()
          }}
        >
          Save
        </Button>
        <Button
          color="secondary"
          onClick={() => {
            toggleContactModal()
            resetCompany()
          }}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ContactModal
